import { Box, Stack, Typography } from "@mui/material";
import { StrengthWheel } from "../meshStrength/StrengthWheel";
import type { ContextMeshAnalysis } from "../meshStrength/contextMeshAnalysis";
import type { ProductBuildSummary, ProductContextMeshStatus } from "./types";

interface ContextMeshScoreProps {
    analysis?: ContextMeshAnalysis | null;
    score?: number | null;
    status?: ProductContextMeshStatus;
    buildState?: ProductBuildSummary["state"];
    compact?: boolean;
}

const unavailableLabel = (status?: ProductContextMeshStatus): string => {
    if (status === "baseline") return "Baseline needed";
    if (status === "analyzing") return "Analyzing…";
    if (status === "loading") return "Loading";
    return "Unavailable";
};

/** Compact Product Hub presentation of the shared analyzer-backed wheel. */
const ContextMeshScore = ({ analysis, score, status, buildState, compact = false }: ContextMeshScoreProps) => {
    const size = compact ? 32 : 36;
    const buildBlocksScore = buildState === "processing" || buildState === "failed" || buildState === "not_started";
    const statusAllowsScore = status === "ready";
    const displayAnalysis = buildBlocksScore || !statusAllowsScore ? null : analysis;
    const displayStatus = buildState === "processing"
        ? "analyzing"
        : buildState === "failed" || buildState === "not_started"
          ? "unavailable"
          : status;
    const label = unavailableLabel(displayStatus);
    const displayScore = displayAnalysis?.score ?? (statusAllowsScore ? score : null);
    return (
        <Stack direction="row" alignItems="center" spacing={0.75} data-testid="product-context-mesh">
            <Box sx={{ width: size, height: size, flex: "0 0 auto" }}>
                {displayScore != null ? (
                    <StrengthWheel
                        score={displayScore}
                        tier={displayAnalysis?.tier}
                        categoryScores={displayAnalysis?.categoryScores}
                        uid={displayAnalysis ? `product-hub-${displayAnalysis.productId}` : "product-hub-summary"}
                        showHub={false}
                        showLabels={false}
                    />
                ) : (
                    <Box role="img" aria-label={`Context Mesh ${label.toLowerCase()}`} sx={{ width: "100%", height: "100%", borderRadius: "50%", bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }} />
                )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                {!compact && <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }}>Context Mesh</Typography>}
                <Typography sx={{ fontSize: compact ? 11 : 13, fontWeight: 700, color: displayScore != null ? "text.primary" : "text.secondary", lineHeight: 1.25, whiteSpace: "nowrap" }}>
                    {displayScore != null ? Math.round(displayScore) : label}
                </Typography>
            </Box>
        </Stack>
    );
};

export default ContextMeshScore;
